'use client'

// import Button from '@/components/inputs/Button'
import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { IFeedView } from '@/interfaces/IFeedView'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { useTranslations } from 'next-intl'
import style from './FeedNav.module.scss'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import setLocale from '@/modules/langService/setLocale'
import clientGetLocale from '@/modules/langService/clientGetLocale'

// import styles from './FeedNav.module.scss'

interface FeedNavProps {
  mainFilter: { type: string, value: string } | null
  view: IFeedView | null
  forYouClicked: () => void
}

function FeedNav ({ mainFilter, view, forYouClicked }: FeedNavProps) {
  const [value, setValue] = useState(0)
  const [languageSelection, setLanguageSelection] = useState('en')
  const [languages, setLanguages] = useState<{ key: string, label: string }[]>([])
  const router = useRouter()
  const t = useTranslations('header')

  const extractLocale = (url: string) => {
    const pathArray = url.split('/')

    if (pathArray[1].length !== 2) return null

    // Assuming the locale is always the first part of the path
    // and that there is always a leading slash in the URL.
    return pathArray[1] || null // Default to 'en' if no locale is found
  }

  useEffect(() => {
    let sortedLanguages = [
      { key: 'my', label: t('global-languages.my') },
      { key: 'vi', label: t('global-languages.vi') },
      { key: 'he', label: t('global-languages.he') }
    ].sort((a, b) => a.label.localeCompare(b.label))

    // inject to the beginning as primary languages
    sortedLanguages = [
      { key: 'en', label: t('global-languages.en') },
      { key: 'es', label: t('global-languages.es') },
      ...sortedLanguages
    ]

    setLanguages(sortedLanguages)
    // if the locale is different than the URL
    const extractedLocaleVal = extractLocale(window.location.pathname)
    const cookieLocale = clientGetLocale()
    const locale = extractedLocaleVal || cookieLocale

    if (extractedLocaleVal) {
      if (extractedLocaleVal !== cookieLocale) {
        setLocale(extractedLocaleVal)
      }
    }

    setLanguageSelection(locale)
  }, [])

  const handleChange = (event: SelectChangeEvent) => {
    const lang = event.target.value as string
    // set the cookie
    setLocale(lang)
    window.location.href = `/${lang}/feed`
  }

  return (
    <>
      {(!mainFilter || mainFilter.type === 'tab') && view &&
        <div className={style.wrapper}>
          <Tabs
            value={value}
            variant="scrollable"
            aria-label="feed-nav-tabs"
            scrollButtons='auto'
            allowScrollButtonsMobile
            style={{ marginBottom: '1.5rem' }}
            className={style.tabWrapper}
          >
            <Tab
              label={t('for-you')}
              value={0}
              style={{ textTransform: 'none' }}
              className={value === 0 ? style.tabSelected : style.tabNotSelected}
              onClick={() => {
                setValue(0)
                forYouClicked()
              }}
            />
            {view.categories.sort((a, b) => a.index - b.index).map((category, i) => {
              const tabVal = category.name.toLowerCase().replaceAll(' ', '-')
              return (
                <Tab
                  key={i}
                  label={category.name}
                  value={i + 1}
                  style={{ textTransform: 'none' }}
                  className={value === i + 1 ? style.tabSelected : style.tabNotSelected}
                  onClick={() => {
                    setValue(i + 1)
                    // navigate to ?tab=category.name
                    router.push(`?tab=${tabVal}`)
                  }}
                />
              )
            })}
          </Tabs>
          <div className={style.rightDropdown}>
            <Select
              id="language-select"
              value={languageSelection}
              onChange={handleChange}
              className={style.selectEle}
              autoWidth
              sx={{
                fontSize: '0.875rem',
                color: 'var(--secondary-font-rgb)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none' // Hides the border
                },
                '& .MuiOutlinedInput-input': {
                  padding: '12px 16px', // Adjust padding as needed
                  fontSize: '0.875rem'
                },
                '& .MuiSelect-icon': {
                  color: 'var(--secondary-font-rgb)' // Changes the color of the dropdown carrot
                }
              }}
            >
              {languages.map(((langOpt, i: number) => {
                return (
                  <MenuItem key={i} value={langOpt.key}>{langOpt.label}</MenuItem>
                )
              }))}
            </Select>
          </div>
        </div>
      }
    </>
  )
}

export default FeedNav

